<template>
  <div :class="['container']">
    <base-input
      :id="id"
      :label="label"
      :placeholder="placeholder"
      :type="type"
      :optional="optional"
      :value="value"
      :name="name"
      :error-message="errorMessage"
      :validate="validate"
      :disabled="disabled"
      :char-limit="charLimit"
      :data-cy="dataCy"
      :no-container="noContainer"
      :autocomplete-disabled="false"
      @changeValue="handleChange"
      @onKeyUp="onKeyUp"
      @inputBlurred="handleBlur"
      @inputFocused="openDropdown"
      @onClick="openDropdown"
    />
    <div
      v-if="dropdownOpen"
      ref="dropdown"
      :class="[
        'custom-datalist',
        {
          'custom-datalist--scrollable': scrollable,
          'custom-datalist--align-right': align === 'right',
        },
      ]"
      role="listbox"
    >
      <slot :close-dropdown="closeDropdown" :active-index="activeOptionIndex" />
    </div>
  </div>
</template>

<script setup lang="ts">
import BaseInput from '@/components/generics/base/BaseInput.vue'
import { debounce } from 'lodash-es'
import { nextTick, onUnmounted, ref, watch } from 'vue'

type InputEventPayload = {
  value: string
  key: number
}

const props = withDefaults(
  defineProps<{
    listName: string
    label?: string
    placeholder: string
    type?: string
    optional?: boolean
    value?: string | null
    name: string
    errorMessage?: string | null
    validate?: boolean
    disabled?: boolean
    charLimit?: number | null
    dataCy?: string
    id?: string | null
    noContainer?: boolean
    scrollable?: boolean
    align?: 'left' | 'right'
    options?: readonly unknown[]
  }>(),
  {
    label: '',
    type: 'text',
    optional: false,
    value: null,
    errorMessage: null,
    validate: false,
    disabled: false,
    charLimit: null,
    dataCy: '',
    id: null,
    noContainer: false,
    scrollable: false,
    align: 'left',
    options: () => [],
  }
)

const emit = defineEmits<{
  (event: 'changeValueFreeText', payload: InputEventPayload): void
  (event: 'onKeyUp', value: string): void
  (event: 'optionSelected', index: number): void
}>()

const ARROW_UP_KEY_CODE = 38
const ARROW_DOWN_KEY_CODE = 40
const ENTER_KEY_CODE = 13

const dropdown = ref<HTMLElement | null>(null)
const dropdownOpen = ref(false)
const activeOptionIndex = ref(-1)

const openDropdown = (): void => {
  dropdownOpen.value = true
}

const closeDropdown = (): void => {
  dropdownOpen.value = false
  activeOptionIndex.value = -1
}

const moveActiveOption = (offset: number): void => {
  if (props.options.length === 0) return

  const nextIndex = activeOptionIndex.value + offset
  activeOptionIndex.value = Math.min(
    Math.max(nextIndex, 0),
    props.options.length - 1
  )
}

// A new result set means the previously active row is gone, so drop the
// selection and return the list to the top rather than leaving the viewer
// scrolled into the middle of results they have not seen.
watch(
  () => props.options,
  async () => {
    activeOptionIndex.value = -1
    await nextTick()
    if (dropdown.value) dropdown.value.scrollTop = 0
  }
)

const handleBlur = debounce(closeDropdown, 200)

const handleChange = (payload: InputEventPayload): void => {
  emit('changeValueFreeText', payload)
}

const onKeyUp = (payload: InputEventPayload): void => {
  if (payload.key === ARROW_DOWN_KEY_CODE) {
    openDropdown()
    moveActiveOption(1)
    return
  }

  if (payload.key === ARROW_UP_KEY_CODE) {
    moveActiveOption(-1)
    return
  }

  if (payload.key === ENTER_KEY_CODE && activeOptionIndex.value >= 0) {
    emit('optionSelected', activeOptionIndex.value)
    closeDropdown()
    return
  }

  activeOptionIndex.value = -1
  emit('onKeyUp', payload.value)
}

onUnmounted(() => handleBlur.cancel())
</script>

<style lang="scss" scoped>
.container {
  position: relative;
}

.custom-datalist {
  position: absolute;
  top: $spacing-2xl;
  left: 0;
  min-width: 350px;
  width: 100%;
  background-color: $color-canvas;
  border: 1px solid $color-border;
  border-radius: $border-radius-m;
  z-index: 3;

  &--scrollable {
    max-height: 320px;
    overflow-y: auto;
  }

  &--align-right {
    left: auto;
    right: 0;
  }
}
</style>
